import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Edit2, Trash2, Shield, Lock, ArrowLeft, CheckCircle } from 'lucide-react'

export default function FirewallManagementPage() {
  const navigate = useNavigate()
  const [rules, setRules] = useState([
    { id: 1, name: 'Allow SSH', protocol: 'TCP', port: '22', action: 'Allow', status: 'Active', priority: 'High' },
    { id: 2, name: 'Allow HTTP', protocol: 'TCP', port: '80', action: 'Allow', status: 'Active', priority: 'Medium' },
    { id: 3, name: 'Allow HTTPS', protocol: 'TCP', port: '443', action: 'Allow', status: 'Active', priority: 'High' },
    { id: 4, name: 'Block Telnet', protocol: 'TCP', port: '23', action: 'Block', status: 'Active', priority: 'Critical' },
    { id: 5, name: 'Allow DNS', protocol: 'UDP', port: '53', action: 'Allow', status: 'Active', priority: 'Medium' },
  ])

  const [showModal, setShowModal] = useState(false)
  const [editingRule, setEditingRule] = useState(null)
  const [formData, setFormData] = useState({ name: '', protocol: 'TCP', port: '', action: 'Allow', status: 'Active', priority: 'Medium' })

  const stats = useMemo(() => ({
    totalRules: rules.length,
    activeRules: rules.filter(r => r.status === 'Active').length,
    allowRules: rules.filter(r => r.action === 'Allow').length,
    blockRules: rules.filter(r => r.action === 'Block').length
  }), [rules])

  const handleCreate = () => {
    setEditingRule(null)
    setFormData({ name: '', protocol: 'TCP', port: '', action: 'Allow', status: 'Active', priority: 'Medium' })
    setShowModal(true)
  }

  const handleEdit = (rule) => {
    setEditingRule(rule)
    setFormData(rule)
    setShowModal(true)
  }

  const handleSave = () => {
    if (!formData.name || !formData.port) return alert('Rule name and port required')
    if (editingRule) {
      setRules(rules.map(r => r.id === editingRule.id ? { ...r, ...formData } : r))
    } else {
      setRules([...rules, { id: Date.now(), ...formData }])
    }
    setShowModal(false)
  }

  const handleDelete = (id) => {
    if (confirm('Delete this firewall rule?')) {
      setRules(rules.filter(r => r.id !== id))
    }
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'bg-red-600/20 text-red-400'
      case 'High': return 'bg-orange-600/20 text-orange-400'
      case 'Medium': return 'bg-yellow-600/20 text-yellow-400'
      case 'Low': return 'bg-green-600/20 text-green-400'
      default: return 'bg-slate-600/20 text-slate-400'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="max-w-7xl mx-auto">
        <button onClick={() => navigate('/network')} className="flex items-center gap-2 text-blue-400 hover:text-blue-300 mb-6 font-semibold">
          <ArrowLeft size={20} /> Back to Network Management
        </button>

        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">Firewall Management</h1>
              <p className="text-slate-400">Configure and monitor firewall rules and policies</p>
            </div>
            <button onClick={handleCreate} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold">
              <Plus size={20} /> Add Rule
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Total Rules</div>
            <div className="text-3xl font-bold text-blue-400">{stats.totalRules}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Active</div>
            <div className="text-3xl font-bold text-green-400">{stats.activeRules}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Allow Rules</div>
            <div className="text-3xl font-bold text-cyan-400">{stats.allowRules}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Block Rules</div>
            <div className="text-3xl font-bold text-red-400">{stats.blockRules}</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-blue-500/20 bg-slate-900/50">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Rule Name</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Protocol</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Port</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Action</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Priority</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => (
                  <tr key={rule.id} className="border-b border-blue-500/10 hover:bg-slate-800/50">
                    <td className="px-6 py-4 text-white font-medium">{rule.name}</td>
                    <td className="px-6 py-4 text-slate-400">{rule.protocol}</td>
                    <td className="px-6 py-4 text-slate-400">{rule.port}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded text-xs font-semibold ${rule.action === 'Allow' ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'}`}>
                        {rule.action}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 rounded text-xs font-semibold bg-blue-600/20 text-blue-400">{rule.status}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded text-xs font-semibold ${getPriorityColor(rule.priority)}`}>{rule.priority}</span>
                    </td>
                    <td className="px-6 py-4 flex gap-2">
                      <button onClick={() => handleEdit(rule)} className="p-2 hover:bg-blue-500/20 rounded">
                        <Edit2 size={16} className="text-blue-400" />
                      </button>
                      <button onClick={() => handleDelete(rule.id)} className="p-2 hover:bg-red-500/20 rounded">
                        <Trash2 size={16} className="text-red-400" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg border border-blue-500/20 p-8 max-w-2xl w-full mx-4">
              <h2 className="text-2xl font-bold text-white mb-6">{editingRule ? 'Edit Rule' : 'Add Firewall Rule'}</h2>
              <div className="space-y-4 mb-6">
                <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Rule Name" className="w-full bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white placeholder-slate-500" />
                <div className="grid grid-cols-2 gap-4">
                  <select value={formData.protocol} onChange={(e) => setFormData({...formData, protocol: e.target.value})} className="bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white">
                    <option>TCP</option>
                    <option>UDP</option>
                    <option>ICMP</option>
                  </select>
                  <input type="text" value={formData.port} onChange={(e) => setFormData({...formData, port: e.target.value})} placeholder="Port" className="bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <select value={formData.action} onChange={(e) => setFormData({...formData, action: e.target.value})} className="bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white">
                    <option>Allow</option>
                    <option>Block</option>
                    <option>Drop</option>
                  </select>
                  <select value={formData.priority} onChange={(e) => setFormData({...formData, priority: e.target.value})} className="bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white">
                    <option>Low</option>
                    <option>Medium</option>
                    <option>High</option>
                    <option>Critical</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setShowModal(false)} className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded font-semibold">Cancel</button>
                <button onClick={handleSave} className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold">Save</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
